import type { BacktestReplenishmentRule } from '@einhaus/project314-types';
import Button from '@components/UI/Button';
import FormNumberInput from '@components/UI/FormNumberInput';
import FormSelect from '@components/UI/FormSelect';

export type CashAdditionRuleDraft = Omit<BacktestReplenishmentRule, 'balanceThreshold' | 'replenishmentAmount' | 'maxReplenishments'> & {
    balanceThreshold: number | null;
    replenishmentAmount: number | null;
    maxReplenishments: number | null;
};
export function cashAdditionRulesFromDrafts(drafts: readonly CashAdditionRuleDraft[]): BacktestReplenishmentRule[] | undefined {
    const result: BacktestReplenishmentRule[] = [];
    for (const draft of drafts) {
        const { balanceThreshold, replenishmentAmount, maxReplenishments } = draft;
        if (
            balanceThreshold === null ||
            replenishmentAmount === null ||
            maxReplenishments === null ||
            !Number.isFinite(balanceThreshold) ||
            balanceThreshold < 0 ||
            !Number.isFinite(replenishmentAmount) ||
            replenishmentAmount <= 0 ||
            !Number.isInteger(maxReplenishments) ||
            maxReplenishments <= 0
        )
            return undefined;
        result.push({ ...draft, balanceThreshold, replenishmentAmount, maxReplenishments });
    }
    return result;
}
interface Props {
    rules: CashAdditionRuleDraft[];
    onChange: (rules: CashAdditionRuleDraft[]) => void;
    needsConfirmation?: boolean;
    onConfirm?: () => void;
}
export default function PortfolioBacktestCashAdditions({ rules, onChange, needsConfirmation, onConfirm }: Props) {
    const change = (index: number, patch: Partial<CashAdditionRuleDraft>) =>
        onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
    return (
        <div className="rounded border border-zinc-700 bg-zinc-950/40 p-3 space-y-3">
            <div className="font-medium text-zinc-100">
                Cash additions: {rules.length ? `${rules.length} conditional rule${rules.length === 1 ? '' : 's'}` : 'None'}
            </div>
            <p className="text-xs text-zinc-400">
                These settings affect this simulation only. Cash decline is measured from starting cash, not portfolio drawdown; buying
                positions can trigger a rule. Rules run in the order shown.
            </p>
            {needsConfirmation && (
                <div className="rounded bg-amber-950/40 p-3 text-sm text-amber-200">
                    This older backtest has no verified portfolio cash policy. Choose the rules below or confirm no cash additions before
                    starting.
                    <Button variant="secondary" onClick={onConfirm}>
                        Use these cash-addition settings
                    </Button>
                </div>
            )}
            {rules.map((rule, index) => (
                <div className="grid gap-3 rounded bg-zinc-900 p-3 md:grid-cols-3" key={rule.id}>
                    <FormSelect
                        label="Cash trigger"
                        id={`cash-threshold-type-${rule.id}`}
                        value={rule.balanceThresholdType}
                        options={[
                            { value: 'dollarAmountDecline', label: 'Cash decline ($)' },
                            { value: 'percentageDecline', label: 'Cash decline (%)' }
                        ]}
                        onChange={(event) => {
                            const value = event.target.value;
                            if (value === 'dollarAmountDecline' || value === 'percentageDecline')
                                change(index, { balanceThresholdType: value });
                        }}
                    />
                    <FormNumberInput
                        id={`cash-threshold-${rule.id}`}
                        label="Cash decline threshold"
                        min={0}
                        value={rule.balanceThreshold}
                        onValueChange={(value) => change(index, { balanceThreshold: value })}
                    />
                    <FormSelect
                        label="Addition type"
                        id={`cash-amount-type-${rule.id}`}
                        value={rule.replenishmentType}
                        options={[
                            { value: 'dollarAmount', label: 'Add dollars' },
                            { value: 'percentage', label: 'Add % of starting cash' }
                        ]}
                        onChange={(event) => {
                            const value = event.target.value;
                            if (value === 'dollarAmount' || value === 'percentage') change(index, { replenishmentType: value });
                        }}
                    />
                    <FormNumberInput
                        id={`cash-amount-${rule.id}`}
                        label="Amount to add"
                        min={0}
                        value={rule.replenishmentAmount}
                        onValueChange={(value) => change(index, { replenishmentAmount: value })}
                    />
                    <FormNumberInput
                        id={`cash-limit-${rule.id}`}
                        label="Maximum additions"
                        integer
                        min={1}
                        value={rule.maxReplenishments}
                        onValueChange={(value) => change(index, { maxReplenishments: value })}
                    />
                    <Button variant="secondary" onClick={() => onChange(rules.filter((_, i) => i !== index))}>
                        Remove rule
                    </Button>
                </div>
            ))}
            {rules.length < 100 && (
                <Button
                    variant="secondary"
                    onClick={() =>
                        onChange([
                            ...rules,
                            {
                                id: crypto.randomUUID(),
                                balanceThresholdType: 'dollarAmountDecline',
                                balanceThreshold: 50000,
                                replenishmentType: 'dollarAmount',
                                replenishmentAmount: 50000,
                                maxReplenishments: 10
                            }
                        ])
                    }
                >
                    Add cash rule
                </Button>
            )}
        </div>
    );
}
